import { invoiceDueDate, invoiceIssueDate, isInvoiceOverdue, invoicePaymentLabel } from "../utils/invoiceDueDate";
import { INVOICE_STATUS_FILTERS, invoiceMatchesStatus } from "../utils/invoiceFilters";
import ClientTabHeading from "./clients/ClientTabHeading";
import { clientInvoices } from "../utils/clientInvoices";
import { outstandingInvoices } from "../utils/invoiceOutstanding";
import { invoiceMonthlyTotals, invoiceMonthLabel } from "../utils/invoiceMonthlyTotals";
import { highestInvoiceFirst } from "../utils/invoiceSort";
import PageHeading from "../components/PageHeading";
import SearchPending from "../components/SearchPending";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { createListSearch } from "../utils/listSearch";
import { apiErrorMessage } from "../utils/apiError";
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Search, Filter, Download, Upload, Mail, Calendar, Building, CheckCircle2, Clock, AlertCircle, Eye, DollarSign, Users, Grid3X3, List } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import AxiosInstance from "../axios/AxiosInstance";
import axiosInstance from "../axios/AxiosInstance";
import { Modal, Select, message } from "antd";
import InvoiceFollowUp from "./facture/InvoiceFollowUp";



export default function FacturesPage({ state, client, embedded = false }) {
  const [allBills, setBills] = useState([]);
  const bills = clientInvoices(allBills, client);
  const [, setOriginalBills] = useState([]);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
    const [, setDocumentCount] = useState(0);
      const [isRelance, setIsRelance] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [typeRelance, setTypeRelance] = useState("");
  const [contactEmail, setContactEmail] = useState("");
      const [showContacts, setShowContacts] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoadingBills, setIsLoadingBills] = useState(false);
  const [billsError, setBillsError] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(searchTerm);
  const [totalMonth, setTotalMonth] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const unpaidOnly = state === "impayée" || searchParams.get("impayees") === "1";
  const showUnpaidOnly = () => {
    const next = new URLSearchParams(searchParams);
    next.set("impayees", "1");
    setSearchParams(next);
  };
  const showAllInvoices = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("impayees");
    setSearchParams(next);
  };
  const inputFileRef = useRef();

  // Fonctions utilitaires
  const formatAmount = (amount, decimals = 2) => {
    if (!amount || amount === null || amount === undefined) {
      return "0,00";
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return "0,00";
    }

    return numAmount.toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non renseigné";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "Date invalide";
    }

    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OUI":
        return "bg-green-100 text-green-800";
      case "NON":
        return "bg-red-100 text-red-800";
      case "PARTIEL":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OUI":
        return <CheckCircle2 className="w-4 h-4" />;
      case "NON":
        return <AlertCircle className="w-4 h-4" />;
      case "PARTIEL":
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const monthlyTotals = invoiceMonthlyTotals(bills);
  const monthlyTotal = monthlyTotals.find(row => row.month === totalMonth) || monthlyTotals[0];

  const outstanding = outstandingInvoices(bills.filter(bill => isInvoiceOverdue(bill)));

  // Calcul des statistiques
  const stats = {
    total: bills.length,
    payees: bills.filter(bill => bill.FactureRegle === "OUI").length,
    impayees: outstanding.count,
    partielles: bills.filter(bill => bill.FactureRegle === "PARTIEL").length,
    montantTotal: bills.reduce((total, bill) => total + parseFloat(bill.montantTTC || 0), 0),
    montantPaye: bills.filter(bill => bill.FactureRegle === "OUI").reduce((total, bill) => total + parseFloat(bill.montantTTC || 0), 0),
    montantImpaye: outstanding.amount
  };

  // Filtrage des factures
  const visibleBills = unpaidOnly ? bills.filter((bill) => isInvoiceOverdue(bill)) : bills;
  const activeStatus = INVOICE_STATUS_FILTERS.find((item) => item.value === statusFilter);
  const filteredBills = visibleBills
    .filter((bill) => invoiceMatchesStatus(bill, statusFilter))
    .filter(createListSearch(appliedSearch, ["client.companyName", "client.siret", "client.contactPhone", "client.contactEMail", "client.town", "numeroFacture", "IdFacture", "RaisonSociale", "NomPrenom", "TotalTTC", "DateEcheance", "DateFacture", "candidat.firstname", "candidat.lastname", "FactureRegle", "montantTTC", "dateFacture", "dateEcheance"]))
    .sort(highestInvoiceFirst);

  const handleClick = () => {
    inputFileRef.current.click();
  };

  
  
  const formatIssueDate = (bill) => {
    const issued = invoiceIssueDate(bill);
    return issued ? formatDate(issued) : "Non renseigné";
  };

  
  const formatDateString = (inputDate) => {
    const year = inputDate.substring(0, 4);
    const month = inputDate.substring(4, 6);
    const day = inputDate.substring(6, 8);

    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const controller = new AbortController();
    axiosInstance
      .get("facture/count", { signal: controller.signal })
      .then((res) => setDocumentCount(res.data))
      .catch((error) => {
        const detail = apiErrorMessage(error, "Impossible de compter les factures.");
        if (detail) message.error(detail);
      });

    loadBills(controller);
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
    
  
  
  /*   const getSoldee =  () => {
    alert("ok")
    axiosInstance
    .get(`facture/soldee`)
    .then((res) => {
      setBills(res.data)
    })
    .catch((e) => {
      console.log(e)
    })
  }
 */


  
  
  
  
  
  const sendRelance = () => {
    if (typeRelance.length === 0) {
      message.error("Selectionnez un type de relance");
      return;
    }

    if (contactEmail.length === 0) {
      message.error("Selectionnez un contact");
      return;
    }

    const relance = [
      ...selectedBill.relance,
      { message: typeRelance, date: Date.now(), contactEmail },
    ];
    AxiosInstance.post(`facture/relance/${selectedBill._id}`, {
      relance,
      typeRelance,
      contactEmail,
    })
      .then((res) => {
        const newBills = [...bills];
        newBills[bills.findIndex((bill) => bill._id === res.data._id)] =
          res.data;
        setBills(newBills);
        setIsRelance(false);
        setTypeRelance("");
        setContactEmail("");
        message.success("Relance envoyée !");
      })
      .catch((error) => {
        message.error(apiErrorMessage(error, "Impossible d’envoyer la relance."));
      });
  };

  
  
  const loadBills = (controller = new AbortController()) => {
    setIsLoadingBills(true);
    setBillsError("");
    AxiosInstance.get("facture/freelance", { signal: controller.signal })
      .then((data) => {
        setBills(data.data);
        setOriginalBills(data.data);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const detail = apiErrorMessage(error, "Impossible de charger les factures.");
        if (detail) setBillsError(detail);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingBills(false);
      });
  };

  const onChangeTypeRelance = (e) => {
    setTypeRelance(e);
  };

  return (
    <div className={embedded ? "bg-white text-sm" : "bg-slate-50"}>
      <div className="">
        {/* Header */}
        <motion.div
          className="rounded-2xl mb-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div>{embedded ? <ClientTabHeading title="Factures" description="Retrouvez les factures, avoirs et règlements du client." /> : <PageHeading icon="invoices" title={<> Gestion des Factures </>} description={<> Gérez et suivez toutes vos factures </>} />}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        >




          <motion.div whileHover={{y: -2}} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-600">Total HT du mois</p>
                <select aria-label="Mois du total des factures" value={monthlyTotal?.month || ""} onChange={event => setTotalMonth(event.target.value)} disabled={!monthlyTotals.length} className="mt-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm capitalize text-slate-600">
                  {!monthlyTotals.length && <option value="">Aucune facture datée</option>}
                  {monthlyTotals.map(row => <option key={row.month} value={row.month}>{invoiceMonthLabel(row.month)}</option>)}
                </select>
                <p className="mt-2 text-3xl font-bold text-slate-900">{formatAmount(monthlyTotal?.amountHT || 0)} €</p>
                <p className="mt-1 text-sm text-slate-500">{monthlyTotal?.count || 0} facture{monthlyTotal?.count > 1 ? "s" : ""}</p>
              </div>
              <div className="rounded-lg bg-blue-100 p-3"><FileText className="h-6 w-6 text-blue-600" /></div>
            </div>
          </motion.div>
          <motion.button
            type="button"
            whileHover={{ y: -2 }}
            onClick={showUnpaidOnly}
            aria-pressed={unpaidOnly}
            className={`rounded-xl border bg-white p-6 text-left shadow-sm transition-all duration-200 hover:shadow-md ${unpaidOnly ? "border-red-300 ring-2 ring-red-100" : "border-slate-200"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Factures impayées</p>
                <p className="text-3xl font-bold text-red-500 mt-1">{formatAmount(stats.montantImpaye)} €</p>
                <p className="text-sm text-slate-500 mt-1">{stats.impayees} facture{stats.impayees > 1 ? "s" : ""} impayée{stats.impayees > 1 ? "s" : ""}</p>
                {outstanding.unknown > 0 && <p className="mt-1 text-xs text-slate-500">{outstanding.unknown} solde{outstanding.unknown > 1 ? "s" : ""} à préciser</p>}
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </motion.button>


        </motion.div>

        {billsError && <p role="alert" className="mb-4 rounded-xl bg-rose-50 p-4 text-sm text-rose-700">{billsError}</p>}

        {unpaidOnly && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <p>Affichage limité aux factures impayées après échéance.</p>
            {state !== "impayée" && (
              <button type="button" onClick={showAllInvoices} className="font-semibold text-rose-900 underline">
                Toutes les factures
              </button>
            )}
          </div>
        )}

        {/* Search and Filters */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une facture..."
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 w-full"
                />
              </div>
              <SearchPending pending={searching} />
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsOpenFilter((open) => !open)}
                aria-expanded={isOpenFilter}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${isOpenFilter || statusFilter !== "all" ? "bg-blue-50 text-blue-800" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
              >
                <Filter className="w-4 h-4" />
                <span>Filtres{statusFilter !== "all" ? ` · ${activeStatus?.label}` : ""}</span>
              </motion.button>

              {!embedded && <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleClick}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <Upload className="w-4 h-4" />
                <span>Importer</span>
              </motion.button>}

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2 bg-slate-100 rounded-lg p-1">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === "grid"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <List className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>
          {isOpenFilter && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <span className="text-sm font-medium text-slate-600">Statut</span>
              {INVOICE_STATUS_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  aria-pressed={statusFilter === item.value}
                  onClick={() => setStatusFilter(item.value)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${statusFilter === item.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Bills List/Grid */}
        <motion.div
        >
          {isLoadingBills ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredBills.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{unpaidOnly && statusFilter === "all" && !searchTerm ? "Aucune facture impayée" : "Aucune facture trouvée"}</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== "all" ? "Essayez de modifier vos critères de recherche ou de statut." : unpaidOnly ? "Aucune facture échue n’est en attente de règlement." : "Commencez par créer votre première facture"}
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredBills.map((bill, index) => (
                  <motion.div
                    key={bill._id || index}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 cursor-pointer hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-slate-900 mb-1">
                          Facture #{bill.numeroFacture || bill.IdFacture}
                        </h3>
                        <div className="flex items-center text-sm text-slate-500">
                          <Building className="w-4 h-4 mr-1" />
                          <span>{bill.client?.companyName || "Client non renseigné"}</span>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(isInvoiceOverdue(bill) ? "NON" : bill.FactureRegle === "NON" ? "" : bill.FactureRegle)}`}>
                        {getStatusIcon(bill.FactureRegle)}
                        <span className="ml-1">
                          {invoicePaymentLabel(bill)}
                        </span>
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                        <span>Émise le {formatIssueDate(bill)}</span>
                      </div>

                      <p className="text-sm text-slate-600">Échéance : {formatDate(invoiceDueDate(bill))}</p>

                      <div className="flex items-center text-sm text-slate-600">
                        <DollarSign className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="font-medium">{formatAmount(bill.montantTTC || bill.TotalTTC)} € TTC</span>
                      </div>

                      {bill.candidat && (
                        <div className="flex items-center text-sm text-slate-600">
                          <Users className="w-4 h-4 mr-2 text-slate-400" />
                          <span>{bill.candidat.firstname} {bill.candidat.lastname}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </motion.button>
                        {bill.FactureRegle === "NON" && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setSelectedBill(bill);
                              setIsRelance(true);
                            }}
                            className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-200"
                            title="Relancer"
                          >
                            <Mail className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>
                      
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-center divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Facture
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Échéance</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Commentaire de suivi</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    <AnimatePresence>
                      {filteredBills.map((bill, index) => (
                        <motion.tr
                          key={bill._id || index}
                          className="hover:bg-slate-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-center">
                              <div className="bg-slate-100 p-2 rounded-lg mr-3">
                                <FileText className="w-4 h-4 text-slate-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-slate-900">
                                  #{bill.numeroFacture || bill.IdFacture}
                                </div>
                                {bill.candidat && (
                                  <div className="text-sm text-slate-500">
                                    {[bill.candidat.firstname, bill.candidat.lastname].filter(Boolean).join(" ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {bill.client?.companyName || "Client non renseigné"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {formatIssueDate(bill)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{formatDate(invoiceDueDate(bill))}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">
                              {formatAmount(bill.montantTTC || bill.TotalTTC)} €
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(isInvoiceOverdue(bill) ? "NON" : bill.FactureRegle === "NON" ? "" : bill.FactureRegle)}`}>
                              {getStatusIcon(bill.FactureRegle)}
                              <span className="ml-1">
                                {invoicePaymentLabel(bill)}
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-4 align-top"><InvoiceFollowUp key={bill._id} bill={bill} /></td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                title="Voir"
                              >
                                <Eye className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                title="Télécharger"
                              >
                                <Download className="w-4 h-4" />
                              </motion.button>
                              {bill.FactureRegle === "NON" && (
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => {
                                    setSelectedBill(bill);
                                    setIsRelance(true);
                                  }}
                                  className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-200"
                                  title="Relancer"
                                >
                                  <Mail className="w-4 h-4" />
                                </motion.button>
                              )}
                              
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      {showContacts && (
        <Modal
          width={600}
          open={showContacts}
          onCancel={() => {
            setShowContacts(false);
            setSelectedClient(null);
          }}
        >
          <div className="flex flex-col space-y-3">
            {selectedClient?.contacts?.map((contact, index) => (
              <p key={index}>
                {contact.firstname} {contact.lastname} - {contact.phone}
              </p>
            ))}
          </div>
        </Modal>
      )}

      {isRelance && (
        <Modal
          width={1000}
          footer={null}
          open={isRelance}
          onCancel={() => {
            setIsRelance(false);
            setSelectedBill(null);
          }}
        >
          <div className="flex flex-col my-10 space-y-6 items-center justify-center ">
            <Select
              className="w-96"
              showSearch
              onChange={(e) => onChangeTypeRelance(e)}
              optionFilterProp="children"
              placeholder="Selectionner type de relance"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[
                {
                  value: "Echéance prochaine",
                  label: "Echéance prochaine (NIV 1)",
                  key: "Echéance prochaine",
                },
                {
                  value: "Facture impayée",
                  label: "Facture impayée (NIV 2)",
                  key: "Facture impayée",
                },
                {
                  value: "Mise en demeure",
                  label: "Mise en demeure (NIV 3)",
                  key: "Mise en demeure",
                },
              ]}
            />

            <Select
              className="w-96"
              showSearch
              onChange={(e) => setContactEmail(e)}
              value={contactEmail.length > 0 ? contactEmail : null}
              optionFilterProp="children"
              placeholder="Selectionner interlocuteur"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={selectedBill.client.contacts?.map((contact, idx) => {
                return {
                  value: contact.email,
                  label: contact.email,
                  key: idx,
                };
              })}
            />
            {typeRelance === "Facture impayée" && (
              <div className="w-3/5 flex justify-center">
                <p>
                  Cher Client,<br></br>
                  <br></br>
                  Nous vous informons que la facture numéro{" "}
                  <span className="font-semibold">
                    {selectedBill.IdFacture}
                  </span>
                  ,d'un montant de{" "}
                  <span className="font-semibold">
                    {selectedBill.TotalTTC}&euro;
                  </span>
                  ,<br></br>
                  émise le{" "}
                  <span className="font-semibold">
                    {formatIssueDate(selectedBill)}
                  </span>
                  , demeure actuellement impayée.<br></br>
                  <br></br>
                  Nous sollicitons votre prompte attention pour régler cette
                  facture dans les meilleurs délais.<br></br>
                  <br></br>
                  Veuillez trouver ci-dessous nos coordonnées bancaires pour
                  faciliter le règlement : [Coordonnées bancaires].<br></br>
                  <br></br>
                  Nous restons à votre disposition pour toute clarification ou
                  information supplémentaire.<br></br>
                  <br></br>
                  Cordialement,
                </p>
              </div>
            )}

            {typeRelance === "Echéance prochaine" && (
              <div className="w-3/5 flex justify-center">
                <p>
                  Cher client,<br></br>
                  <br></br>
                  Nous tenons à vous rappeler que la date d'échéance de votre
                  facture n°
                  <span className="font-semibold">
                    {selectedBill.IdFacture}
                  </span>{" "}
                  approche à grands pas{" "}
                  <span className="font-semibold">
                    ({formatDateString(selectedBill.DateEcheance)})
                  </span>
                  . <br></br>
                  <br></br>
                  Pour faciliter le processus de paiement et éviter tout retard,
                  nous souhaitons vous informer de l'importance de régler vos
                  factures à temps.<br></br>
                  <br></br>
                  Si vous avez des questions concernant le montant ou les
                  modalités de paiement, n'hésitez pas à contacter notre équipe.
                  <br></br>
                  <br></br>
                  Nous vous remercions par avance de votre diligence dans le
                  règlement de vos factures et de votre confiance en notre
                  entreprise.<br></br>
                  <br></br>
                  Cordialement,
                </p>
              </div>
            )}

            {typeRelance === "Mise en demeure" && (
              <div className="w-3/5 flex justify-center">
                <p>
                  Cher client,<br></br>
                  <br></br>
                  Je prends l'initiative de vous écrire en tant que représentant
                  de {selectedBill.agency.name} concernant la facture impayée à
                  votre nom, émises conformément à nos termes contractuels.
                  <br></br>
                  <br></br>
                  Malgré tous nos appels antérieurs et nos efforts pour résoudre
                  cette situation à l'amiable, nous constatons que le règlement
                  de la facture suivante demeure en souffrance :<br></br>
                  <br></br>- facture n°
                  <span className="font-semibold">
                    {selectedBill.IdFacture}
                  </span>{" "}
                  :{" "}
                  <span className="font-semibold">{selectedBill.TotalTTC}</span>
                  € TTC<br></br>
                  <br></br>
                  Conformément aux termes de l’article L. 441-6 du Code de
                  commerce, le montant des factures est augmenté de pénalités de
                  retard au taux d'intérêt légal.<br></br>
                  <br></br>
                  Le montant est également augmenté de la clause pénale, d’un
                  montant forfaitaire de 40€.<br></br>
                  <br></br>
                  Nous vous mettons en demeure de régler la somme de{" "}
                  <span className="font-semibold">{selectedBill.TotalTTC}</span>
                  €, toutes taxes comprises (TTC), augmenté des pénalités de
                  retard précitées, sous{" "}
                  <span className="font-semibold">48h</span> à compter de la
                  réception du présent e-mail.<br></br>
                  <br></br>
                  Pour régulariser votre situation et payer votre dette sans
                  frais supplémentaires, il convient d’effectuer un virement sur
                  le compte bancaire de notre société, ou de nous adresser un
                  chèque.<br></br>
                  <br></br>
                  Voici nos coordonnées bancaires : IBAN :{" "}
                  {selectedBill.agency.iban} BIC : {selectedBill.agency.BIC}
                  <br></br>
                  <br></br>A défaut, nous serons dans l’obligation de saisir les
                  juridictions compétentes d’une demande de condamnation à votre
                  encontre, laquelle sera augmentée des frais notamment de
                  justice et de procédure exposés inutilement pour se défendre.
                  Une déclaration pour impayé sera aussi faite auprès de notre
                  assurance crédit.<br></br>
                  <br></br>
                  Vous devez considérer le présent comme une mise en demeure de
                  nature à faire courir tout droit et intérêt que la loi et les
                  tribunaux attachent aux mises en demeure.<br></br>
                  <br></br>
                  Dans l’attente de votre règlement, recevez, Cher client,
                  l’expression de nos sentiments distingués.<br></br>
                  <br></br>
                  Cordialement,
                </p>
              </div>
            )}

            {/* <button
              onClick={sendRelance}
              className="bg-mountain-500 w-96 font-semibold p-2 rounded-md text-white"
            >
              Modifier le message
            </button> */}

            <button
              onClick={sendRelance}
              className="bg-mountain-500 w-96 font-semibold p-2 rounded-md text-white"
            >
              Envoyer la relance
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
